import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { CmsWorkspaceProvider } from "../context/CmsWorkspaceContext";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui";
import { formatCmsDateTime } from "./CmsShared";

export interface CmsInspectorItem {
  title: string;
  subtitle?: string;
  module?: string;
  createdAt?: string;
  updatedAt?: string;
  published?: boolean;
  author?: string;
  previewUrl?: string;
  meta?: Array<{ label: string; value: string }>;
}

interface CmsInspectorContextValue {
  item: CmsInspectorItem | null;
  openInspector: (item: CmsInspectorItem) => void;
  closeInspector: () => void;
}

const CmsInspectorContext = createContext<CmsInspectorContextValue | null>(null);

export function CmsInspectorProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<CmsInspectorItem | null>(null);
  const openInspector = useCallback((next: CmsInspectorItem) => setItem(next), []);
  const closeInspector = useCallback(() => setItem(null), []);

  return (
    <CmsInspectorContext.Provider value={{ item, openInspector, closeInspector }}>
      {children}
      <CmsInspectorPanel />
    </CmsInspectorContext.Provider>
  );
}

export function useCmsInspector() {
  const ctx = useContext(CmsInspectorContext);
  if (!ctx) throw new Error("useCmsInspector must be used within CmsInspectorProvider");
  return ctx;
}

/** Route layout — wraps CMS pages with workspace + inspector providers. */
export function CmsInspectorRoute() {
  return (
    <CmsWorkspaceProvider>
      <CmsInspectorProvider>
        <Outlet />
      </CmsInspectorProvider>
    </CmsWorkspaceProvider>
  );
}

function CmsInspectorPanel() {
  const { item, closeInspector } = useCmsInspector();
  const location = useLocation();

  useEffect(() => {
    closeInspector();
  }, [location.pathname, closeInspector]);

  return (
    <AnimatePresence>
      {item ? (
        <>
          <motion.button
            type="button"
            className="cms-inspector__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeInspector}
            aria-label="Close inspector"
          />
          <motion.aside
            className="cms-inspector"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 36 }}
            role="dialog"
            aria-label="Content inspector"
          >
            <header className="cms-inspector__head">
              <div>
                <p className="cms-inspector__module">{item.module ?? "CMS"}</p>
                <h3>{item.title}</h3>
                {item.subtitle ? <p>{item.subtitle}</p> : null}
              </div>
              <Button variant="ghost" size="sm" onClick={closeInspector} aria-label="Close">
                <X size={16} />
              </Button>
            </header>
            <div className="cms-inspector__body">
              {item.previewUrl ? (
                <div className="cms-inspector__preview">
                  <img src={item.previewUrl} alt="" />
                </div>
              ) : null}
              <dl className="cms-inspector__meta">
                {item.published !== undefined ? (
                  <div>
                    <dt>Published</dt>
                    <dd>{item.published ? "Yes" : "Draft"}</dd>
                  </div>
                ) : null}
                {item.author ? (
                  <div>
                    <dt>Author</dt>
                    <dd>{item.author}</dd>
                  </div>
                ) : null}
                {item.createdAt ? (
                  <div>
                    <dt>Created</dt>
                    <dd>{formatCmsDateTime(item.createdAt)}</dd>
                  </div>
                ) : null}
                {item.updatedAt ? (
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatCmsDateTime(item.updatedAt)}</dd>
                  </div>
                ) : null}
                {item.meta?.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
